import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Input, Rheostat, Form } from "@oxygen/core";
import getThemeClasses from "@oxygen/theme";
import { CSSFlex as Flex } from "GlobalComponent/CSSCore";
import { validateLowerLimit, validateUpperLimit } from "../utils/Validations";
import { getUsedCarLandingTrackingObject } from "../utils/Tracking";
import {
  TRACKING_OBJECT,
  LOWER_BUDGET_RANGE_LIMITS,
  BUDGET_RANGE_LIMITS,
} from "../constants";
import styles from "./scss/PriceRange.module.scss";
import { fireInteractiveTracking } from "GlobalUtils/Analytics";
import { trackCustomData } from "GlobalUtils/cwTrackingPwa";
import { Minus } from "@oxygen/icons";
import { platform as PLATFORM } from "GlobalEnum/Platform";
import {
  getLowerBudgetValue,
  getUpperBudgetValue,
} from "../utils/UsedLandingUtils";

const validationMessageClasses = getThemeClasses(`
  pt-1;
  font-size-6;
  color-red-lightest;
  display-block;
  height-8;
`);

const getBudgetSectionContainerClasses = platform => {
  return getThemeClasses(`
  border-style-solid;
  border-color-whiteSmoke;
  border-radius-3;
  border-width-1;
  pt-4 md:pt-10;
  pl-5;
  pr-5;
  mb-4 md:mb-0;
  mt-4 md:mt-0;
  bg-white;
  md: border-radius-2;
  ${platform.id === PLATFORM.DESKTOP.id ? styles.priceRangeContainer : ""}
`);
};

const getLowerBudgetText = lowerBudgetLimit => {
  const lowerBudgetLimitValue =
    lowerBudgetLimit === ""
      ? LOWER_BUDGET_RANGE_LIMITS.min
      : Math.min(Number(lowerBudgetLimit), LOWER_BUDGET_RANGE_LIMITS.max);
  return lowerBudgetLimitValue > BUDGET_RANGE_LIMITS.max
    ? BUDGET_RANGE_LIMITS.max
    : lowerBudgetLimitValue;
};
const getUpperBudgetText = upperBudgetLimit => {
  return upperBudgetLimit === ""
    ? BUDGET_RANGE_LIMITS.max
    : Number(upperBudgetLimit);
};
const getCategory = (
  lowerBudgetLimit,
  upperBudgetLimit,
  latestLowerLimit,
  latestUpperLimit
) => {
  const lowerLimitBudgetValue = getLowerBudgetValue(lowerBudgetLimit);
  const upperLimitBudgetValue = getUpperBudgetValue(upperBudgetLimit);
  if (latestLowerLimit !== lowerLimitBudgetValue) {
    return TRACKING_OBJECT.LOWER_BUDGET;
  }
  if (upperLimitBudgetValue !== latestUpperLimit) {
    return TRACKING_OBJECT.UPPER_BUDGET;
  }
  return "";
};

const budgetTextClasses = getThemeClasses(`
  font-size-8;
  color-charcoal;
  pb-3;
`);

const rangeClasses = getThemeClasses(`
  color-gainsBoroGray;
  width-4;
  ml-1;
  mr-1;
`);

const alignBudgetInputClasses = getThemeClasses(`
  align-items-center;
  display-flex;
`);

const budgetLabelClasses = getThemeClasses(`
  mt-1;
  color-darkGray;
  font-size-6;
  font-weight-semibold;
  justify-content-space-between;
`);

const budgetRheostatClasses = getThemeClasses(`
  mb-4;
`);

const snapPointRange = Array.from({ length: 20 }, (_, x) => x + 1);

const PriceRangeSelection = ({
  handleBudgetLimit,
  handleLowerBudgetValidation,
  handleUpperBudgetValidation,
  showChooseBudgetText,
  lowerBudget = "",
  upperBudget = "",
  platform = PLATFORM.MOBILE,
}) => {
  const [lowerBudgetLimit, setLowerBudgetLimit] = useState(lowerBudget);
  const [upperBudgetLimit, setUpperBudgetLimit] = useState(upperBudget);
  const [lowerBudgetValidation, setLowerBudgetValidation] = useState({
    isValid: true,
    message: "",
  });
  const [upperBudgetValidation, setUpperBudgetValidation] = useState({
    isValid: true,
    message: "",
  });

  const [shouldUpdateSliderValue, setShouldUpdateSliderValue] = useState(true);

  //lower budget limit varies between 0-19
  const handleLowerBudgetChange = event => {
    setLowerBudgetLimit(event.target.value);
    handleBudgetLimit(event.target.value, upperBudgetLimit);
    setShouldUpdateSliderValue(false);
  };
  //upper budget limit varies between lowerbudget+1 and above
  const handleUpperBudgetChange = event => {
    setUpperBudgetLimit(event.target.value);
    handleBudgetLimit(lowerBudgetLimit, event.target.value);
    setShouldUpdateSliderValue(false);
  };

  const validateLowerLimitBudget = useCallback(() => {
    let validation = validateLowerLimit(lowerBudgetLimit);
    setLowerBudgetValidation(validation);
    handleLowerBudgetValidation(validation.isValid);
  }, [lowerBudgetLimit]);

  const validateUpperLimitBudget = useCallback(() => {
    let validation = validateUpperLimit(lowerBudgetLimit, upperBudgetLimit);
    setUpperBudgetValidation(validation);
    handleUpperBudgetValidation(validation.isValid);
  }, [lowerBudgetLimit, upperBudgetLimit]);

  // validation of input fields
  useEffect(() => {
    validateLowerLimitBudget();
    validateUpperLimitBudget();
  }, [validateLowerLimitBudget, validateUpperLimitBudget]);

  const handleSliderChangeTracking = useCallback(
    (latestLowerLimit, latestUpperLimit) => {
      let category = getCategory(
        lowerBudgetLimit,
        upperBudgetLimit,
        latestLowerLimit,
        latestUpperLimit
      );
      const trackingObject = getUsedCarLandingTrackingObject(
        category,
        latestLowerLimit,
        latestUpperLimit
      );
      fireInteractiveTracking(
        trackingObject.gaCat,
        trackingObject.gaActClick,
        trackingObject.gaLblClick
      );
      trackCustomData(
        trackingObject.bhriguCat,
        trackingObject.bhriguActClick,
        trackingObject.bhriguLblClick
      );
    },
    [lowerBudgetLimit, upperBudgetLimit]
  );

  const handleBudgetSliderUpdate = useCallback(
    budgetObject => {
      const { values } = budgetObject;
      let [min, max] = values;
      if (!shouldUpdateSliderValue) {
        setShouldUpdateSliderValue(true);
        return;
      }
      max =
        upperBudgetLimit >= max && max === BUDGET_RANGE_LIMITS.max
          ? upperBudgetLimit
          : max;
      handleSliderChangeTracking(min, max);
      setLowerBudgetLimit(min);
      setUpperBudgetLimit(max);
      handleBudgetLimit(min, max);
    },
    [shouldUpdateSliderValue, handleSliderChangeTracking]
  );

  const getMinMaxSliderValue = useMemo(() => {
    let lowerBudgetValue = getLowerBudgetText(lowerBudgetLimit);
    let budgetRange = [lowerBudgetValue];
    let upperBudgetLimitValue = getUpperBudgetText(upperBudgetLimit);
    budgetRange.push(Math.max(lowerBudgetValue + 1, upperBudgetLimitValue));
    return budgetRange;
  }, [lowerBudgetLimit, upperBudgetLimit]);

  return (
    <>
      <div className={getBudgetSectionContainerClasses(platform)}>
        {showChooseBudgetText && (
          <p className={budgetTextClasses} data-testing-id="choose-your-budget">
            Choose Your Budget
          </p>
        )}
        <div className={budgetRheostatClasses}>
          <Rheostat
            data-testing-id="budget-slider"
            min={BUDGET_RANGE_LIMITS.min}
            max={BUDGET_RANGE_LIMITS.max}
            values={getMinMaxSliderValue}
            onChange={handleBudgetSliderUpdate}
            snapPoints={snapPointRange}
            pitPointSubLabel={
              <Flex className={budgetLabelClasses}>
                <p>0</p>
                <p>20+ Lakh</p>
              </Flex>
            }
          />
        </div>
        <Form className={alignBudgetInputClasses}>
          <Form.Item
            className={styles.formItem}
            validateStatus={lowerBudgetValidation.isValid ? "" : "error"}
          >
            <Input
              data-testing-id="budget-lower-limit"
              suffix="Lakh"
              value={lowerBudgetLimit}
              onChange={handleLowerBudgetChange}
              type="number"
              placeholder="Min"
            />
          </Form.Item>
          <Minus className={rangeClasses} />
          <Form.Item
            className={styles.formItem}
            validateStatus={upperBudgetValidation.isValid ? "" : "error"}
          >
            <Input
              data-testing-id="budget-upper-limit"
              suffix="Lakh"
              value={upperBudgetLimit}
              onChange={handleUpperBudgetChange}
              type="number"
              placeholder="Max"
            />
          </Form.Item>
        </Form>
        <div className={validationMessageClasses}>
          {upperBudgetValidation.message || lowerBudgetValidation.message}
        </div>
      </div>
    </>
  );
};

export default PriceRangeSelection;
